import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Loader2, Paperclip, X, Eye, ExternalLink, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYouEmailModal = ({ isOpen, onClose, eventId, token, recipientCount, propertyDescription, propertyFiles = [] }) => {
  const [subject, setSubject] = useState('Thank You for Visiting Our Open House');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Track which files to include in email (by file id/url)
  const [excludedFileIds, setExcludedFileIds] = useState(new Set());
  // Newly uploaded files in this session (also stored in S3)
  const [sessionFiles, setSessionFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Pre-populate with property description when modal opens
  React.useEffect(() => {
    if (isOpen && propertyDescription) {
      setCustomMessage(propertyDescription);
    }
    if (!isOpen) {
      setShowPreview(false);
      setPreviewHtml('');
      setExcludedFileIds(new Set());
      setSessionFiles([]);
    }
  }, [isOpen, propertyDescription]);

  // All files = property files + session uploaded files
  const allFiles = [...propertyFiles, ...sessionFiles];
  const includedFiles = allFiles.filter(f => !excludedFileIds.has(f.id));

  const toggleFileExclusion = (fileId) => {
    setExcludedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        // Upload to S3 via existing endpoint (general, no property_id)
        const res = await axios.post(`${API}/files/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        setSessionFiles(prev => [...prev, {
          id: res.data.id,
          original_filename: res.data.original_filename,
          file_url: res.data.file_url,
        }]);
        toast.success(`Uploaded: ${file.name}`);
      } catch (err) {
        toast.error(`Failed: ${file.name} — ${err.response?.data?.detail || 'Upload error'}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getFileLinksPayload = () => {
    return includedFiles.map(f => ({
      filename: f.original_filename,
      url: f.file_url,
    }));
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/email-preview`, {
        custom_message: customMessage || '',
        file_links: getFileLinksPayload(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewHtml(res.data.html_preview);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/events/${eventId}/send-thankyou-emails`,
        {
          event_id: eventId,
          subject: subject,
          custom_message: customMessage,
          file_links: getFileLinksPayload(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.mode === 'test') {
        toast.success(
          `${response.data.message}`,
          { description: response.data.note, duration: 8000 }
        );
      } else {
        toast.success(
          `Successfully sent ${response.data.emails_sent} email(s)`,
          {
            description: response.data.failed > 0
              ? `${response.data.failed} email(s) failed to send`
              : 'All emails sent successfully',
            duration: 5000
          }
        );
      }

      onClose();
      setSubject('Thank You for Visiting Our Open House');
      setCustomMessage('');
      setSessionFiles([]);
      setExcludedFileIds(new Set());
    } catch (error) {
      toast.error('Failed to send emails', {
        description: error.response?.data?.detail || 'Please try again later'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`sm:max-w-[600px] max-h-[90vh] flex flex-col p-0 ${showPreview ? '[&>button]:hidden' : ''}`}>
        {showPreview && previewHtml ? (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200 flex-row items-center justify-between">
              <DialogTitle className="text-xl sm:text-2xl font-semibold text-[#1A4D2E] flex items-center gap-2">
                <Eye size={20} className="sm:block hidden" />
                Email Preview
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(false)}
                data-testid="close-preview-btn"
                className="rounded-full"
              >
                <X size={16} className="mr-1" />
                Close Preview
              </Button>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <div
                className="bg-white p-4"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </>
        ) : (
          <>
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-stone-200">
          <DialogTitle className="text-xl sm:text-2xl font-semibold text-[#1A4D2E] flex items-center gap-2">
            <Mail size={20} className="sm:block hidden" />
            Send Thank You Emails
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex-1">
              <p className="text-sm text-stone-600">
                <strong className="text-stone-800">Recipients:</strong> {recipientCount} unique visitor(s)
              </p>
            </div>
            <button
              type="button"
              onClick={handlePreview}
              disabled={loadingPreview}
              data-testid="preview-email-link"
              className="ml-3 flex items-center gap-1.5 text-sm font-semibold text-[#1A4D2E] hover:text-[#143D24] hover:underline shrink-0"
            >
              {loadingPreview ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
              Preview Email
            </button>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="subject" className="text-sm font-medium text-stone-700">
              Email Subject <span className="text-red-500">*</span>
            </Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              className="h-10"
              disabled={sending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="custom-message" className="text-sm font-medium text-stone-700">
              Property Details Only
            </Label>
            <Textarea
              id="custom-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Enter property details..."
              rows={4}
              className="resize-none text-sm"
              disabled={sending}
            />
          </div>

          {/* File Links Section */}
          <div className="space-y-1.5" data-testid="email-files-section">
            <Label className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
              <Paperclip size={14} />
              File Links in Email
            </Label>

            {/* List of all files with toggle X */}
            {allFiles.length > 0 && (
              <div className="space-y-1.5">
                {allFiles.map((f) => {
                  const isExcluded = excludedFileIds.has(f.id);
                  return (
                    <div
                      key={f.id}
                      className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
                        isExcluded
                          ? 'bg-stone-100 border-stone-200 opacity-50'
                          : 'bg-stone-50 border-stone-200'
                      }`}
                      data-testid={`email-file-item-${f.id}`}
                    >
                      <a
                        href={f.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 text-sm font-medium truncate flex-1 mr-2 ${
                          isExcluded ? 'text-stone-400 line-through' : 'text-[#1A4D2E] hover:underline'
                        }`}
                        data-testid={`email-file-link-${f.id}`}
                      >
                        <ExternalLink size={14} className="flex-shrink-0" />
                        {f.original_filename}
                      </a>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFileExclusion(f.id)}
                        disabled={sending}
                        className={`flex-shrink-0 h-7 w-7 p-0 ${
                          isExcluded
                            ? 'hover:bg-green-100 hover:text-green-600 text-stone-400'
                            : 'hover:bg-red-100 hover:text-red-600 text-stone-500'
                        }`}
                        data-testid={`email-file-toggle-${f.id}`}
                        title={isExcluded ? 'Include in email' : 'Exclude from email'}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload new file to S3 */}
            <div
              className={`border-2 border-dashed border-stone-300 rounded-lg p-3 hover:border-[#1A4D2E] transition-colors cursor-pointer ${(sending || uploading) ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => !(sending || uploading) && fileInputRef.current?.click()}
              data-testid="email-upload-area"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={sending || uploading}
                style={{ display: 'none' }}
                data-testid="email-file-upload-input"
              />
              <div className="flex flex-col items-center justify-center">
                {uploading ? (
                  <>
                    <Loader2 size={20} className="text-stone-400 mb-1 animate-spin" />
                    <span className="text-xs text-stone-600 font-medium">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload size={20} className="text-stone-400 mb-1" />
                    <span className="text-xs text-stone-600 font-medium">Click to upload files</span>
                    <span className="text-xs text-stone-500">Files will be saved to storage and linked in email</span>
                  </>
                )}
              </div>
            </div>

            {includedFiles.length > 0 && (
              <p className="text-xs text-stone-500">{includedFiles.length} file link(s) will be included in the email</p>
            )}
          </div>

        </div>

        <DialogFooter className="px-6 py-4 border-t border-stone-200 flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={sending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sending || !subject.trim()}
            className="bg-[#1A4D2E] hover:bg-[#143D24] text-white flex-1"
          >
            {sending ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2" size={16} />
                Send Emails
              </>
            )}
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouEmailModal;
